import { supabase } from "../../Services/supabaseClient";

const defaultStages = [
  {
    name: "New Opportunity",
    position: 1,
    default_probability: 10,
    stage_type: "open",
    color_key: "teal",
  },
  {
    name: "Discovery",
    position: 2,
    default_probability: 25,
    stage_type: "open",
    color_key: "gold",
  },
  {
    name: "Proposal",
    position: 3,
    default_probability: 50,
    stage_type: "open",
    color_key: "teal",
  },
  {
    name: "Negotiation",
    position: 4,
    default_probability: 75,
    stage_type: "open",
    color_key: "gold",
  },
  {
    name: "Won",
    position: 5,
    default_probability: 100,
    stage_type: "won",
    color_key: "teal",
  },
  {
    name: "Lost",
    position: 6,
    default_probability: 0,
    stage_type: "lost",
    color_key: "gray",
  },
];

export const ensureCrmSettings = async (companyId) => {
  const { data: existingSettings, error: settingsError } = await supabase
    .from("crm_settings")
    .select("id")
    .eq("company_id", companyId)
    .limit(1);

  if (settingsError) {
    throw settingsError;
  }

  if ((existingSettings || []).length > 0) {
    return existingSettings[0];
  }

  const { data: createdSettings, error: createSettingsError } = await supabase
    .from("crm_settings")
    .insert([
      {
        company_id: companyId,
        stale_opportunity_days: 14,
        collections_followup_days: 3,
        auto_create_collection_cases: true,
        default_quote_terms: "Payment due within 7 days",
        default_quote_notes: "Thank you for choosing KostPadi.",
      },
    ])
    .select()
    .single();

  if (createSettingsError) {
    throw createSettingsError;
  }

  return createdSettings;
};

export const ensureCrmPipeline = async (companyId) => {
  const { data: existingPipelines, error: pipelineError } = await supabase
    .from("crm_pipelines")
    .select("id, name, is_default, created_at")
    .eq("company_id", companyId)
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: true });

  if (pipelineError) {
    throw pipelineError;
  }

  let pipeline = (existingPipelines || [])[0] || null;

  if (!pipeline) {
    const { data: createdPipeline, error: createPipelineError } = await supabase
      .from("crm_pipelines")
      .insert([
        {
          company_id: companyId,
          name: "Default Sales Pipeline",
          is_default: true,
        },
      ])
      .select()
      .single();

    if (createPipelineError) {
      throw createPipelineError;
    }

    pipeline = createdPipeline;
  }

  if (!pipeline.is_default) {
    const { data: updatedPipeline, error: updatePipelineError } = await supabase
      .from("crm_pipelines")
      .update({ is_default: true })
      .eq("id", pipeline.id)
      .eq("company_id", companyId)
      .select()
      .single();

    if (updatePipelineError) {
      throw updatePipelineError;
    }

    pipeline = updatedPipeline;
  }

  const { data: existingStages, error: stagesError } = await supabase
    .from("crm_pipeline_stages")
    .select("id, name")
    .eq("pipeline_id", pipeline.id)
    .order("position", { ascending: true });

  if (stagesError) {
    throw stagesError;
  }

  if ((existingStages || []).length === 0) {
    const stageRows = defaultStages.map((stage) => ({
      pipeline_id: pipeline.id,
      name: stage.name,
      position: stage.position,
      default_probability: stage.default_probability,
      stage_type: stage.stage_type,
      color_key: stage.color_key,
    }));

    const { error: insertStagesError } = await supabase
      .from("crm_pipeline_stages")
      .insert(stageRows);

    if (insertStagesError) {
      throw insertStagesError;
    }
  }

  return pipeline;
};

export const bootstrapCrmForCompany = async (companyId) => {
  await ensureCrmSettings(companyId);
  const pipeline = await ensureCrmPipeline(companyId);
  return { pipeline };
};
